import {
    alpha,
    Box,
    Button,
    Grid,
    InputAdornment,
    OutlinedInput,
    Typography,
    useTheme,
} from '@mui/material';
import {
    useLocale,
    getLocaleNumberFormatNoDecimals,
    formatCurrencyNoDecimalsIfPossible,
    getCurrencySymbolOrCode,
} from '@repo/i18n';
import { currencyAtom } from '@repo/widget-utils/currencyAtom';
import { capitalize } from '@repo/common-utils/TextUtils';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import { useAtom } from 'ximple';

import type { JSX } from 'react';

interface ValueProps {
    giftcardValue: number;
    onChange: (value: number) => void;
    showError: boolean;
}

export default function CreateGiftcardValue(props: ValueProps): JSX.Element {
    const { giftcardValue, onChange, showError } = props;
    const { t, locale } = useLocale();
    const theme = useTheme();

    const [currency] = useAtom(currencyAtom);
    const configuration = useConfigurations();

    const priceIncrements =
        configuration.giftCardPriceIncrements ??
        (currency.currency === 'nok' ? [50, 100, 500, 1000] : [10, 25, 50, 100]);

    return (
        <Grid container direction="column" m={`${theme.spacing(2)} 0 ${theme.spacing(6)} 0`}>
            <Grid>
                <Typography color="textSecondary" variant="body1">
                    <Box component="span" fontSize="14px">
                        {capitalize(t.value)}
                    </Box>
                </Typography>
            </Grid>
            <Grid>
                <OutlinedInput
                    fullWidth
                    error={showError}
                    sx={{
                        backgroundColor: theme.palette.common.white,
                        '& input': {
                            textAlign: 'right',
                        },
                    }}
                    value={
                        giftcardValue > 0
                            ? getLocaleNumberFormatNoDecimals(locale, giftcardValue)
                            : ''
                    }
                    onChange={(e) => handleInputChange(e, onChange)}
                    startAdornment={
                        <InputAdornment position="start">
                            <Typography
                                sx={{
                                    color: theme.palette.common.black,
                                    opacity: 0.4,
                                    backgroundColor: theme.palette.common.white,
                                    '& input': {
                                        textAlign: 'right',
                                    },
                                }}
                            >
                                {getCurrencySymbolOrCode(currency, locale)}
                            </Typography>
                        </InputAdornment>
                    }
                />
            </Grid>
            <Grid container direction="row" justifyContent="flex-end">
                <Grid>
                    <Box mt={theme.spacing(1)}>
                        {priceIncrements.map((price) => {
                            return (
                                <AddPriceButton
                                    key={price}
                                    price={price}
                                    giftcardValue={giftcardValue}
                                    onChange={onChange}
                                />
                            );
                        })}
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    );
}

function AddPriceButton({
    price,
    giftcardValue,
    onChange,
}: {
    price: number;
    giftcardValue: ValueProps['giftcardValue'];
    onChange: ValueProps['onChange'];
}): JSX.Element {
    const { locale } = useLocale();
    const [currency] = useAtom(currencyAtom);
    const theme = useTheme();

    return (
        <Button
            sx={{
                backgroundColor: theme.palette.common.white,
                borderRadius: '0',
                borderColor: theme.palette.divider,
                '&.Mui-disabled': {
                    border: `1px solid ${theme.palette.action.disabledBackground}`,
                },
                '&:hover': {
                    borderColor: theme.palette.divider,
                    backgroundColor: alpha(
                        theme.palette.text.primary,
                        theme.palette.action.hoverOpacity,
                    ),
                },

                '&:first-of-type': {
                    borderRadius: `${theme.spacing(1)} 0 0 ${theme.spacing(1)}`,
                },

                '&:last-of-type': {
                    borderRadius: `0 ${theme.spacing(1)} ${theme.spacing(1)} 0`,
                },

                '&:nth-of-type(n+2) ': {
                    '&.MuiButton-outlined': {
                        borderLeft: '0',
                    },
                },
                [theme.breakpoints.down('md')]: {
                    '&.MuiButton-root': {
                        minWidth: theme.spacing(6),
                        padding: '5px 10px',
                    },
                },
            }}
            variant="outlined"
            key={`pre-defined-price-increment-${price}`}
            onClick={() => onChange(giftcardValue + price)}
        >
            <Typography variant="body1">
                + {formatCurrencyNoDecimalsIfPossible(locale, currency, price)}
            </Typography>
        </Button>
    );
}

function handleInputChange(
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    onChange: (value: number) => void,
): void {
    const { value } = e.currentTarget;
    // Removes everything except numbers
    const newValue = value.replace(/[^\d]/g, '');

    if (newValue.length > 12) return;

    if (newValue.length > 0) {
        const numValue = parseInt(newValue);
        if (numValue >= 0) {
            return onChange(numValue);
        }
    }
    return onChange(0);
}
